import { Edit } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { DateCell } from 'components/Table/Cells/DateCell';
import { SimpleTextCell } from 'components/Table/Cells/SimpleTextCell';
import { DateFormats } from 'constants/common';
import { Column } from 'react-table';
import { theme } from 'theme';
import { Broadcast, BroadcastAttachment, BroadcastEditorModes, BroadcastFormValues } from '../types';
import { StatusCell } from '../ui';
import { FileObject } from 'mui-file-dropzone';
import { Nullable } from 'global';
import { BroadcastStatus, BroadcastType, UpsertBroadcastInput } from '@trnsact/trnsact-shared-types';

export const getBroadcastEditorBreadcrumbLabel = (mode: BroadcastEditorModes) => {
  switch (mode) {
    case BroadcastEditorModes.Create:
      return 'Create Broadcast';
    case BroadcastEditorModes.Edit:
      return 'Edit Broadcast';
    default:
      return '';
  }
};

export const mapFileToBroadcastAttachment = (file: FileObject): BroadcastAttachment => ({
  name: file.file.name,
  source: '',
});

export const getBroadcastsTableColumns = (
  isSuperAdmin: boolean,
  handleEditBroadcast: (broadcastId: string) => void
): Column<Broadcast>[] => [
  {
    sortable: false,
    Header: 'Title',
    accessor: 'title',
    resizable: false,
    headerClassName: 'customHeader',
    minWidth: 250,
    Cell: ({ original }: { original: Broadcast }) => <SimpleTextCell value={original.title} />,
  },
  {
    sortable: false,
    Header: 'Type',
    accessor: 'type',
    resizable: false,
    headerClassName: 'customHeader',
    Cell: ({ original }: { original: Broadcast }) => <SimpleTextCell value={original.type} />,
  },
  {
    sortable: false,
    Header: 'Status',
    accessor: 'status',
    resizable: false,
    headerClassName: 'customHeader',
    Cell: ({ original }: { original: Broadcast }) => <StatusCell status={original.status} />,
  },
  {
    sortable: false,
    Header: 'Effective Date',
    accessor: 'effectiveDateTime',
    resizable: false,
    headerClassName: 'customHeader',
    Cell: ({ original }: { original: Broadcast }) => (
      <DateCell value={original.effectiveDateTime} format={DateFormats.Short} />
    ),
  },
  {
    sortable: false,
    Header: 'Is Global',
    accessor: 'isGlobal',
    resizable: false,
    headerClassName: 'customHeader',
    Cell: ({ original }: { original: Broadcast }) => <SimpleTextCell value={original.accountId ? '-' : 'Yes'} />,
    show: isSuperAdmin,
  },
  {
    sortable: false,
    Header: 'Action',
    accessor: 'action',
    resizable: false,
    headerClassName: 'customHeader',
    width: 100,
    Cell: ({ original }: { original: Broadcast }) => (
      <Box gap={1}>
        <IconButton
          color="primary"
          size="small"
          onClick={() => handleEditBroadcast(original.broadcastId)}
          sx={{ borderRadius: '4px', border: `1px solid ${theme.palette.primary.main}` }}
        >
          <Edit color="primary" />
        </IconButton>
      </Box>
    ),
  },
];

export const getUpsertBroadcastInput = (
  broadcastId: Nullable<string>,
  accountId: string,
  status: BroadcastStatus,
  formValues: BroadcastFormValues
): UpsertBroadcastInput => ({
  broadcastId,
  title: formValues.title,
  type: formValues.type,
  effectiveDateTime: new Date(formValues.effectiveDateTime).toISOString(),
  body: formValues.body,
  status: status,
  accountId: formValues.isGlobal ? undefined : accountId,
  // we'll pass multiple audienceInput objects in future
  audienceInput: formValues.isGlobal
    ? [
        {
          audienceType: formValues.audienceType!,
        },
      ]
    : undefined,
});

export const getBroadcastTypeOptions = (isSuperAdmin: boolean) => [
  { label: 'Product', value: BroadcastType.Product },
  { label: 'Program', value: BroadcastType.Program },
  { label: 'Compliance', value: BroadcastType.Compliance },
  { label: 'Company', value: BroadcastType.Company },
  { label: 'Promotion', value: BroadcastType.Promotion },
  ...(isSuperAdmin ? [{ label: 'System', value: BroadcastType.System }] : []),
];
